import axios from 'axios';
import { PaymentRequest, PaymentResponse } from '../types/payment.types';

// Base URL for payment API - this would come from environment variables in a real app
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.masafinance.com';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const processPayment = async (request: PaymentRequest): Promise<PaymentResponse> => {
  try {
    // Simulate API delay for demo purposes
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // In a real implementation, this would make an actual API call
    // const response = await api.post('/payments/process', request);
    // return response.data;
    
    // Mock successful payment response
    const mockResponse: PaymentResponse = {
      success: true,
      transactionId: `TXN_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      status: 'completed',
      message: 'Payment processed successfully',
    };
    
    // Simulate occasional failures for testing
    if (Math.random() < 0.1) { // 10% chance of failure
      return {
        success: false,
        transactionId: '',
        status: 'failed',
        message: 'Payment failed. Please try again.',
      };
    }
    
    return mockResponse;
  } catch (error) {
    console.error('Payment processing error:', error);
    
    return {
      success: false,
      transactionId: '',
      status: 'failed',
      message: 'An error occurred while processing your payment. Please try again.',
    };
  }
};

export const validatePaymentMethod = async (methodId: string): Promise<boolean> => {
  try {
    // Mock validation - in real app, this would validate with the payment processor
    await new Promise(resolve => setTimeout(resolve, 500));
    return true;
  } catch (error) {
    console.error('Payment method validation error:', error);
    return false;
  }
};

export const getPaymentStatus = async (transactionId: string): Promise<PaymentResponse> => {
  try {
    // const response = await api.get(`/payments/status/${transactionId}`);
    // return response.data;
    
    // Mock status check
    return {
      success: true,
      transactionId,
      status: 'completed',
      message: 'Payment completed successfully',
    };
  } catch (error) {
    console.error('Payment status check error:', error);
    
    return {
      success: false,
      transactionId,
      status: 'failed',
      message: 'Could not retrieve payment status',
    };
  }
};

export const refundPayment = async (transactionId: string, amount?: number): Promise<PaymentResponse> => {
  try {
    // const response = await api.post(`/payments/refund`, {
    //   transactionId,
    //   amount,
    // });
    // return response.data;
    
    // Mock refund response
    return {
      success: true,
      transactionId: `REFUND_${transactionId}`,
      status: 'completed',
      message: 'Refund processed successfully',
    };
  } catch (error) {
    console.error('Payment refund error:', error);
    
    return {
      success: false,
      transactionId,
      status: 'failed',
      message: 'Refund processing failed',
    };
  }
}; 